import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import s from '../../pages/CadastroClientes.module.css';
import { fmtMoeda, DIAS_VENCIMENTO, rotuloDia, rotuloConta } from './recorrenciaUtil';

/* Aba Recorrência (Cadastro de Clientes) — o que ESTE cliente paga todo mês:
   serviço, valor, dia de vencimento, banco/layout do boleto e o que gerar.
   Quem gera de fato é Vendas → Gerar Recorrência (junta todos os clientes). */

const LINHA_VAZIA = {
  id: null, produto_id: '', valor: '', dia_vencimento: 10, conta_boleto_id: '',
  gerar_boleto: true, gerar_nfse: true, enviar_email: true, ativo: true, observacao: '',
};

export default function AbaRecorrencia({ cliente }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const clienteId = cliente?.id;
  const [edicao, setEdicao] = useState(null);
  const [erro, setErro] = useState('');

  const { data, isFetching, isError, error } = useQuery({
    queryKey: ['recorrencias', 'cliente', clienteId],
    queryFn: () => api.get('/recorrencias', { params: { cliente_id: clienteId } }).then(r => r.data),
    enabled: !!clienteId && clienteId !== 1,
  });
  const { data: servicos = [] } = useQuery({
    queryKey: ['servicos-recorrencia'],
    queryFn: () => api.get('/produtos', { params: { tipo: 'serviço', todos: 'true', limite: 500 } })
      .then(r => r.data?.rows || r.data || []),
  });
  const { data: contas = [] } = useQuery({
    queryKey: ['contas-banco-recorrencia'],
    queryFn: () => api.get('/financeiro/contas')
      .then(r => (r.data || []).filter(c => c.ativo && c.tipo === 'banco')),
  });

  if (!clienteId) return <div className={s.abaPlaceholder}>Salve o cadastro do cliente para configurar a recorrência.</div>;
  if (clienteId === 1) return <div className={s.abaPlaceholder}>Venda Consumidor não pode ter recorrência — localize um cliente cadastrado.</div>;

  const lista = data?.recorrencias || [];
  const atualizar = () => qc.invalidateQueries({ queryKey: ['recorrencias'] });

  const escolherServico = (id) => {
    const sv = servicos.find(x => String(x.id) === String(id));
    setEdicao(e => ({ ...e, produto_id: id, valor: e.valor || (sv ? Number(sv.preco_venda) || '' : '') }));
  };

  const salvar = async () => {
    setErro('');
    const corpo = {
      ...edicao,
      cliente_id: clienteId,
      produto_id: Number(edicao.produto_id) || null,
      valor: Number(String(edicao.valor).replace(',', '.')),
      dia_vencimento: Number(edicao.dia_vencimento),
      conta_boleto_id: edicao.conta_boleto_id ? Number(edicao.conta_boleto_id) : null,
    };
    try {
      if (edicao.id) await api.put(`/recorrencias/${edicao.id}`, corpo);
      else await api.post('/recorrencias', corpo);
      setEdicao(null);
      atualizar();
    } catch (e) {
      setErro(e.response?.data?.erro || 'Não foi possível salvar a recorrência.');
    }
  };

  const excluir = async (r) => {
    if (!window.confirm(`Excluir a recorrência "${r.produto_nome}"?`)) return;
    setErro('');
    try { await api.delete(`/recorrencias/${r.id}`); atualizar(); }
    catch (e) { setErro(e.response?.data?.erro || 'Não foi possível excluir.'); }
  };

  const check = (campo) => (
    <input type="checkbox" checked={!!edicao[campo]} onChange={e => setEdicao(x => ({ ...x, [campo]: e.target.checked }))} />
  );

  const linhaEdicao = edicao && (
    <tr key="edicao" style={{ background: '#f0f6ff' }}>
      <td>
        <select className={s.selectErp} style={{ width: '100%' }} value={edicao.produto_id}
          onChange={e => escolherServico(e.target.value)}>
          <option value="">— serviço —</option>
          {servicos.map(sv => <option key={sv.id} value={sv.id}>{sv.nome}</option>)}
        </select>
      </td>
      <td>
        <input className={s.inputErp} style={{ width: 90, textAlign: 'right' }} value={edicao.valor}
          onChange={e => setEdicao(x => ({ ...x, valor: e.target.value }))} placeholder="0,00" />
      </td>
      <td>
        <select className={s.selectErp} value={edicao.dia_vencimento}
          onChange={e => setEdicao(x => ({ ...x, dia_vencimento: e.target.value }))}>
          {DIAS_VENCIMENTO.map(d => <option key={d} value={d}>{rotuloDia(d)}</option>)}
        </select>
      </td>
      <td>
        <select className={s.selectErp} style={{ width: '100%' }} value={edicao.conta_boleto_id || ''}
          onChange={e => setEdicao(x => ({ ...x, conta_boleto_id: e.target.value }))}>
          <option value="">Conta padrão da loja</option>
          {contas.map(c => <option key={c.id} value={c.id}>{rotuloConta(c)}</option>)}
        </select>
      </td>
      <td style={{ textAlign: 'center' }}>{check('gerar_boleto')}</td>
      <td style={{ textAlign: 'center' }}>{check('gerar_nfse')}</td>
      <td style={{ textAlign: 'center' }}>{check('enviar_email')}</td>
      <td style={{ textAlign: 'center' }}>{check('ativo')}</td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={salvar}>Salvar</button>{' '}
        <button className={s.btnErp} onClick={() => { setEdicao(null); setErro(''); }}>Cancelar</button>
      </td>
    </tr>
  );

  const marca = v => (v ? '✓' : '');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5 }}>
        <span>O que <strong>{cliente.nome}</strong> paga todo mês.</span>
        <button className={s.btnErp} onClick={() => { setErro(''); setEdicao({ ...LINHA_VAZIA }); }} disabled={!!edicao}>
          + Adicionar serviço
        </button>
        <span style={{ flex: 1 }} />
        <button className={s.btnErp} onClick={() => navigate('/vendas/recorrencia')}>Ir para Gerar Recorrência →</button>
      </div>

      {!cliente.email && lista.some(r => r.enviar_email) && (
        <div style={{ fontSize: 12, color: '#e65100' }}>
          Cliente sem e-mail no cadastro (aba Geral) — o envio por e-mail vai falhar até preencher.
        </div>
      )}
      {erro && <div style={{ color: '#c62828', fontSize: 13 }}>{erro}</div>}

      {isError ? (
        <div className={s.abaPlaceholder}>{error?.response?.data?.erro || 'Não foi possível carregar a recorrência.'}</div>
      ) : (
        <div style={{ overflow: 'auto', maxHeight: 360 }}>
          <table className={s.modalTabela}>
            <thead>
              <tr>
                <th>Serviço</th>
                <th style={{ width: 100, textAlign: 'right' }}>Valor</th>
                <th style={{ width: 120 }}>Vencimento</th>
                <th style={{ width: 230 }}>Banco / layout do boleto</th>
                <th style={{ width: 70 }}>Gerar boleto</th>
                <th style={{ width: 70 }}>Gerar NFS-e</th>
                <th style={{ width: 70 }} title="Manda boleto + NFS-e pro e-mail do cliente">Enviar e-mail</th>
                <th style={{ width: 50 }}>Ativo</th>
                <th style={{ width: 130 }}></th>
              </tr>
            </thead>
            <tbody>
              {edicao && !edicao.id && linhaEdicao}
              {lista.map(r => (edicao?.id === r.id ? linhaEdicao : (
                <tr key={r.id} style={{ opacity: r.ativo ? 1 : 0.55 }}>
                  <td style={{ cursor: 'default' }}>{r.produto_nome}</td>
                  <td style={{ cursor: 'default', textAlign: 'right' }}>{fmtMoeda(r.valor)}</td>
                  <td style={{ cursor: 'default' }}>{rotuloDia(r.dia_vencimento)}</td>
                  <td style={{ cursor: 'default' }}>{r.conta_nome || 'Conta padrão da loja'}</td>
                  <td style={{ cursor: 'default', textAlign: 'center' }}>{marca(r.gerar_boleto)}</td>
                  <td style={{ cursor: 'default', textAlign: 'center' }}>{marca(r.gerar_nfse)}</td>
                  <td style={{ cursor: 'default', textAlign: 'center' }}>{marca(r.enviar_email)}</td>
                  <td style={{ cursor: 'default', textAlign: 'center' }}>{marca(r.ativo)}</td>
                  <td style={{ cursor: 'default', whiteSpace: 'nowrap' }}>
                    <button className={s.btnErp} title="Alterar" disabled={!!edicao}
                      onClick={() => { setErro(''); setEdicao({
                        id: r.id, produto_id: r.produto_id, valor: Number(r.valor), dia_vencimento: r.dia_vencimento,
                        conta_boleto_id: r.conta_boleto_id || '', gerar_boleto: r.gerar_boleto, gerar_nfse: r.gerar_nfse,
                        enviar_email: r.enviar_email, ativo: r.ativo, observacao: r.observacao || '',
                      }); }}>✏️ Alterar</button>{' '}
                    <button className={s.btnErp} title="Excluir" disabled={!!edicao} onClick={() => excluir(r)}>🗑</button>
                  </td>
                </tr>
              )))}
              {!lista.length && !edicao && (
                <tr><td colSpan={9} style={{ cursor: 'default', textAlign: 'center', color: '#6b7280', padding: 18 }}>
                  {isFetching ? 'Carregando...' : 'Este cliente não tem recorrência. Use "+ Adicionar serviço".'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
